// The wildcard draft (sim/cards.ts): three cards on the table, pick one with ✕ or pass with ○. Pad first (←→ or
// the stick moves the cursor; the card lifts), keyboard second. Self-contained like the gun rack (ui/loadout.ts):
// its own DOM, CSS and pad poll, and it blocks game input while it's up; the run keeps going around you (Godot's
// multiplayer rule), so pick quickly. Art: public/assets/cards/<id>.jpg (the Godot cards, downscaled).

import { CARDS, DraftKind, RARITY_NAME, Rarity, handOf } from '../../sim/cards';
import { EL_NAME, gunElem, gunLevel } from '../../sim/elements';
import type { PlayerState } from '../../sim/player';
import { weaponDef } from '../../sim/weapons';
import { firstPad, psGlyph } from '../input/padnav';

let cssDone = false;
const CSS = `
.cd-wrap { position: fixed; inset: 0; z-index: 41; display: flex; align-items: center; justify-content: center;
  background: radial-gradient(ellipse at 50% 110%, rgba(250,199,125,0.16), transparent 60%), rgba(10,12,11,0.5);
  font-family: 'Space Grotesk', system-ui, sans-serif; color: #f0e6c8; }
.cd-panel { width: min(1080px, calc(100vw - 24px)); max-height: calc(100vh - 24px); overflow: auto; padding: 20px 26px 16px;
  background: rgba(14,18,16,0.7); border: 1px solid rgba(250,199,125,0.35); border-radius: 4px; backdrop-filter: blur(6px);
  box-shadow: 0 30px 80px rgba(0,0,0,0.45); }
.cd-title { font-weight: 600; letter-spacing: 0.5px; color: #fac77d; font-size: 30px; margin: 0; line-height: 1; }
.cd-sub { color: #bfb69d; font-size: 14px; margin: 6px 0 14px; letter-spacing: 0.4px; }
.cd-row { display: flex; gap: 22px; justify-content: center; align-items: stretch; }
.cd-card { width: 290px; min-height: 380px; padding: 12px 14px 14px; border-radius: 6px; background: rgba(22,27,24,0.85);
  border: 2px solid rgba(240,200,80,0.45); transition: transform 120ms ease, border-color 120ms ease, opacity 120ms ease; opacity: 0.82; cursor: pointer; }
.cd-card.cur { transform: translateY(-10px) scale(1.045); border-color: #ffd34a; opacity: 1; box-shadow: 0 18px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,211,74,0.35) inset; }
.cd-card.cursed { border-color: rgba(217,51,89,0.6); }
.cd-card.cursed.cur { border-color: #e04468; box-shadow: 0 18px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(224,68,104,0.4) inset; }
.cd-badge { font-size: 11px; letter-spacing: 1.6px; color: #ffd34a; font-weight: 600; }
.cd-card.cursed .cd-badge { color: #e04468; }
.cd-card.trinket .cd-badge { color: #bfb69d; }
.cd-art { width: 100%; height: 150px; margin: 8px 0 10px; border-radius: 3px; background-size: cover; background-position: center; filter: saturate(0.9) contrast(1.05); }
.cd-card.cursed .cd-art { filter: saturate(0.6) sepia(0.3) hue-rotate(-30deg) contrast(1.1); }
.cd-name { font-size: 20px; font-weight: 600; margin: 0 0 6px; color: #f6ead0; }
.cd-desc { font-size: 14px; line-height: 1.35; color: #d8d0b8; }
.cd-fam { margin-top: 10px; font-size: 11px; letter-spacing: 1.2px; color: #8f8a78; text-transform: uppercase; }
.cd-foot { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; color: #bfb69d; font-size: 14px; }
.cd-foot b { color: #f0e6c8; font-weight: 600; }
.cd-glyph { display: inline-block; vertical-align: middle; margin-right: 6px; }
.cd-hand { font-size: 12px; letter-spacing: 0.4px; color: #a9a28c; }
.cd-pass { padding: 6px 12px; border-radius: 3px; border: 1px solid rgba(191,182,157,0.35); color: #bfb69d; cursor: pointer; }
.cd-pass.cur { border-color: #fac77d; color: #fac77d; }
.cd-gun { width: 290px; min-height: 150px; padding: 16px 14px; border-radius: 6px; background: rgba(22,27,24,0.85); border: 2px solid rgba(240,200,80,0.45);
  transition: transform 120ms ease, border-color 120ms ease, opacity 120ms ease; opacity: 0.82; cursor: pointer; }
.cd-gun.cur { transform: translateY(-6px) scale(1.03); border-color: #ffd34a; opacity: 1; }
.cd-gun .cd-name { font-size: 22px; }
.cd-el { font-size: 13px; letter-spacing: 1.4px; color: #8ee3ff; margin-top: 6px; }
`;

export interface CardDraft { close(): void; open: boolean }

/** the badge an element card wears, and what the gun slot reads like ("RIFLE · FIRE L2") */
const elBadge = (c: { elem?: number; l4?: boolean }) => c.l4 ? `LEGENDARY · ${EL_NAME[c.elem!]} L4` : `ELEMENT · ${EL_NAME[c.elem!]}`;
const gunLine = (me: PlayerState, s: number) => { const el = gunElem(me, s); const lv = s === 0 ? me.wl0 : me.wl1; return `${el < 0 ? 'NO ELEMENT' : `${EL_NAME[el]} L${gunLevel(me, s)}`}${lv > 0 ? ` · GUNSMITH LV${lv}` : ''}`; };

/** Put the draft on screen. `offer` is the three card numbers (0 = empty slot); `pick(slot, wslot)` is called with 0..2 or 255 to
 *  pass; an element install adds the gun it goes on (0 primary, 1 secondary) after a second page asks. */
export function openCardDraft(root: HTMLElement, me: PlayerState, kind: DraftKind, pick: (slot: number, wslot?: number) => void): CardDraft {
  if (!cssDone) { const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st); cssDone = true; }
  const offer = [me.offer0, me.offer1, me.offer2].filter((i) => i > 0);
  const wrap = document.createElement('div');
  wrap.className = 'cd-wrap';
  const hand = handOf(me).map((i) => CARDS[i].name);
  wrap.innerHTML = `<div class="cd-panel">
    <h2 class="cd-title">${kind === DraftKind.Rare ? 'BOSS FELLED · A RARE' : kind === DraftKind.Cursed ? 'THE HEXED STRONGBOX · PICK YOUR CURSE' : 'THE BRIDGE DRAFT'}</h2>
    <div class="cd-sub">${kind === DraftKind.Cursed ? 'You opened it. One of these three is yours; the only way out is through. A Rare follows.' : 'Choose one of three, or pass. It lasts the ride and cannot be changed. The posse plays on while you pick.'}</div>
    <div class="cd-row">${offer.map((id, i) => {
      const c = CARDS[id];
      const badge = c.elem !== undefined ? elBadge(c) : c.cursed ? '💀 CURSED' : c.rarity === Rarity.Common ? 'TRINKET' : c.cls !== undefined ? `${RARITY_NAME[c.rarity]} · YOUR CLASS` : RARITY_NAME[c.rarity];
      return `<div class="cd-card${c.cursed ? ' cursed' : ''}${c.rarity === Rarity.Common ? ' trinket' : ''}" data-i="${i}">
        <div class="cd-badge">${badge}</div>
        <div class="cd-art" style="background-image:url('assets/cards/${c.id}.jpg')"></div>
        <div class="cd-name">${c.cursed ? '💀 ' : ''}${c.name}</div>
        <div class="cd-desc">${c.desc}</div>
        <div class="cd-fam">${c.family.replace(/_/g, ' ')}</div>
      </div>`;
    }).join('')}</div>
    <div class="cd-foot">
      <span class="cd-hand">${hand.length ? `YOUR HAND · ${hand.join(' · ')}` : 'YOUR HAND IS EMPTY'}</span>
      <span><span class="cd-glyph">${psGlyph('cross')}</span><b>Take it</b> &nbsp;&nbsp; <span class="cd-pass" data-i="255" style="${kind === DraftKind.Cursed ? 'display:none' : ''}"><span class="cd-glyph">${psGlyph('circle')}</span>Pass</span></span>
    </div>
  </div>`;
  root.appendChild(wrap);
  let cards = [...wrap.querySelectorAll<HTMLElement>('.cd-card')];
  const passEl = wrap.querySelector<HTMLElement>('.cd-pass')!;
  let cur = Math.min(1, cards.length - 1); // Godot starts on the middle card
  const draw = () => { cards.forEach((c, i) => c.classList.toggle('cur', i === cur)); passEl.classList.toggle('cur', cur === cards.length); };
  draw();
  const api: CardDraft = { open: true, close: () => { if (!api.open) return; api.open = false; wrap.remove(); document.removeEventListener('keydown', onKey, true); } };
  // page two (an element install): which gun takes it. ○ here goes back to the cards.
  let pendingSlot = -1;
  const askGun = (slot: number) => {
    pendingSlot = slot;
    const c = CARDS[offer[slot]];
    wrap.querySelector<HTMLElement>('.cd-sub')!.textContent = c.gunsmith ? `${c.name}: which gun gets the work? Up to LV3 each.` : `${c.name}: which gun takes it? The same element again levels it up (to L3); a different one swaps at the level it had.`;
    const row = wrap.querySelector<HTMLElement>('.cd-row')!;
    row.innerHTML = [0, 1].map((s) => `<div class="cd-gun cd-card" data-i="${s}"><div class="cd-badge">${s === 0 ? 'PRIMARY' : 'SECONDARY'}</div><div class="cd-name">${weaponDef(s === 0 ? me.w0 : me.w1).short}</div><div class="cd-el">${gunLine(me, s)}</div></div>`).join('');
    cards = [...row.querySelectorAll<HTMLElement>('.cd-card')];
    cards.forEach((el, i) => { el.onmouseenter = () => { cur = i; draw(); }; el.onclick = () => choose(i); });
    passEl.innerHTML = `<span class="cd-glyph">${psGlyph('circle')}</span>Back`;
    cur = me.wSlot === 1 ? 1 : 0; draw();
  };
  const choose = (slot: number) => {
    if (!api.open) return;
    if (pendingSlot >= 0) { // page two: `slot` is the gun
      if (slot === 255) { if (kind === DraftKind.Cursed) return; api.close(); pick(255); return; }
      api.close(); pick(pendingSlot, slot); return;
    }
    if (slot === 255 && kind === DraftKind.Cursed) return; // no passing a curse
    const c = slot === 255 ? undefined : CARDS[offer[slot]];
    if (c && ((c.elem !== undefined && !c.l4) || c.gunsmith)) { askGun(slot); return; }
    api.close(); pick(slot);
  };
  const confirm = () => choose(cur >= cards.length ? 255 : cur);
  cards.forEach((c, i) => { c.onmouseenter = () => { cur = i; draw(); }; c.onclick = () => choose(i); });
  passEl.onmouseenter = () => { cur = cards.length; draw(); }; passEl.onclick = () => choose(255);
  const move = (d: number) => { cur = Math.max(0, Math.min(kind === DraftKind.Cursed && pendingSlot < 0 ? cards.length - 1 : cards.length, cur + d)); draw(); };
  const onKey = (e: KeyboardEvent) => {
    if (!api.open) return;
    if (e.code === 'ArrowLeft' || e.code === 'KeyA') move(-1);
    else if (e.code === 'ArrowRight' || e.code === 'KeyD') move(1);
    else if (e.code === 'Enter' || e.code === 'Space') confirm();
    else if (e.code === 'Escape') choose(255);
    else return;
    e.preventDefault(); e.stopPropagation();
  };
  document.addEventListener('keydown', onKey, true);
  // the pad: its own poll, buttons held at open are ignored until released (the ✕ that fired the bell can't take a card)
  let held = 0, repeatAt = 0, lastDir = 0, prev = -1;
  const poll = (t: number) => {
    if (!api.open) return;
    const gp = firstPad();
    if (gp) {
      const b = (i: number) => !!gp.buttons[i]?.pressed;
      let mask = 0;
      if (b(0)) mask |= 1; if (b(1)) mask |= 2; if (b(14)) mask |= 16; if (b(15)) mask |= 32;
      const lx = gp.axes[0] ?? 0; if (lx < -0.5) mask |= 16; if (lx > 0.5) mask |= 32;
      if (prev < 0) { held = mask; prev = mask; }
      held &= mask;
      const fresh = mask & ~prev & ~held;
      const dir = mask & 48 & ~held;
      if (dir && (dir !== lastDir || t > repeatAt)) { move(dir & 16 ? -1 : 1); repeatAt = t + (dir !== lastDir ? 320 : 110); lastDir = dir; }
      if (!dir) lastDir = 0;
      if (fresh & 1) confirm();
      else if (fresh & 2) choose(255);
      prev = mask;
    }
    requestAnimationFrame(poll);
  };
  requestAnimationFrame(poll);
  return api;
}
